import { useState, useMemo } from 'react';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';

import {
  DropdownIndicator,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  type OptionType,
} from './components';
import { useAutocompleteLoader } from './useAutocompleteLoader';
import { selectStyles } from './styles';

import type {
  AutoscaleConditionType,
  AutoscaleConditionOperator,
} from '../autoscaleRulesMenuItems';

export default function InputAutocompleteMultiSelect({
  conditionType,
  operator,
  values,
  onChange,
  placeholder = 'Search',
  className,
  disabled = false,
}: {
  conditionType: AutoscaleConditionType;
  operator?: AutoscaleConditionOperator;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}) {
  const [inputValue, setInputValue] = useState('');
  const [defaultOptions, setDefaultOptions] = useState<OptionType[] | true | undefined>(undefined);
  const [isLoadingDefault, setIsLoadingDefault] = useState(false);
  const debouncedLoadOptions = useAutocompleteLoader(conditionType);

  const valueOptions = useMemo<OptionType[]>(
    () => values.map((t) => ({ value: t, label: t })),
    [values]
  );

  const handleChange = (opts: readonly OptionType[] | null) => {
    onChange((opts || []).map((o) => o.value));
  };

  // Determine if this is a "contains" operator that should support free text input
  const isContainsOperator = operator === 'contains' || operator === "doesn't contain";

  // For contains operators, don't use autocomplete - just return empty
  const loadOptionsForContains = () => Promise.resolve([]);

  // Handle input changes for "contains" operators
  const handleInputChange = (newValue: string, actionMeta: { action: string }) => {
    setInputValue(newValue);
    return newValue;
  };

  // Handle Enter key to create pill for "contains" operators
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isContainsOperator) {
      return;
    }

    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      const textToAdd = inputValue.trim();
      if (textToAdd && !values.includes(textToAdd)) {
        onChange([...values, textToAdd]);
        setInputValue('');
      }
    }
  };

  const handleMenuOpen = () => {
    if (!isContainsOperator) {
      // Always reload default options on open (no caching)
      setIsLoadingDefault(true);
      setDefaultOptions(true);
      debouncedLoadOptions('').then((opts) => {
        setDefaultOptions(opts);
        setIsLoadingDefault(false);
      });
    }
  };

  // Use CreatableSelect for "contains" operators, regular AsyncSelect for "is/is not"
  const SelectComponent = isContainsOperator ? AsyncCreatableSelect : AsyncSelect;

  return (
    <SelectComponent<OptionType, true>
      key={conditionType}
      isMulti
      isDisabled={disabled}
      cacheOptions={false}
      defaultOptions={
        isContainsOperator ? [] : defaultOptions === undefined ? false : defaultOptions
      }
      loadOptions={isContainsOperator ? loadOptionsForContains : debouncedLoadOptions}
      onMenuOpen={handleMenuOpen}
      value={valueOptions}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onKeyDown={handleKeyDown}
      inputValue={inputValue}
      placeholder={placeholder}
      className={className}
      components={{
        Option,
        DropdownIndicator,
        IndicatorSeparator: null,
        MultiValue,
        Placeholder,
        NoOptionsMessage: isContainsOperator
          ? () => null
          : () => <NoOptionsMessage inputValue={inputValue} isLoading={isLoadingDefault} />,
      }}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      loadingMessage={() => 'Searching...'}
      noOptionsMessage={({ inputValue: input }) =>
        isLoadingDefault || !input.trim() ? null : 'No results found'
      }
      menuIsOpen={
        isContainsOperator ? (inputValue.trim().length > 0 ? undefined : false) : undefined
      }
      styles={selectStyles}
      // For CreatableSelect: customize how new options are created
      {...(isContainsOperator
        ? {
            formatCreateLabel: (inputValue: string) => `Add "${inputValue}"`,
            // Allow creating options
            isValidNewOption: (inputValue: string) =>
              inputValue.trim().length > 0 && !values.includes(inputValue.trim()),
            // Show "Add [text]" option at the top of the list
            createOptionPosition: 'first' as const,
          }
        : {})}
    />
  );
}
